import {Box, FormHelperText, MenuItem, Stack, TextField} from "@mui/material";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import Property from "crqlar-react-common/dist/components/Property";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import React from "react";
import {useTranslation} from "react-i18next";
import {DEFAULT_PANEL_COLOR} from "../public/signingColors";
import {DEFAULT_HERO_OPACITY} from "../public/signingPhoto";
import {SIGNING_INK, SIGNING_PAPER} from "../public/signingTheme";
import {DigitalSigningCornerStyle, DigitalSigningVariant} from "../public/digitalSigning.types";
import {DigitalSigningDesign, HERO_OPACITY_PRESETS} from "./digitalSigningTemplate.model";

interface Props {
    design: DigitalSigningDesign
    onChange: (design: DigitalSigningDesign) => void
    errors?: {primaryColor?: string, panelColor?: string}
    disabled?: boolean
}

const LAYOUTS: {value: DigitalSigningVariant, label: string}[] = [
    {value: 'PAPER', label: 'Paper'},
    {value: 'HERO', label: 'Hero'},
    {value: 'BACKGROUND', label: 'Background'},
];

// What the photo and the panel color do in each layout that has them, as translation keys
const PHOTO_ROWS = {
    HERO: {
        photo: 'Hero image',
        photoTooltip: 'The photo at the top, shown whole in 16:9. With a photo, the page shows no logo.',
        panelTooltip: 'The band behind the title and the details. Empty: white.',
        panelPlaceholder: DEFAULT_PANEL_COLOR,
    },
    BACKGROUND: {
        photo: 'Background image',
        photoTooltip: 'The photo behind the whole page, under the veil.',
        panelTooltip: 'The veil over the photo. Empty: the paper of the Paper layout.',
        panelPlaceholder: SIGNING_PAPER,
    },
};

const CORNERS: {value: DigitalSigningCornerStyle, label: string}[] = [
    {value: 'SHARP', label: 'Sharp'},
    {value: 'ROUNDED', label: 'Rounded'},
    {value: 'PILL', label: 'Pill'},
];

// The picker hands out #RRGGBBAA; the backend stores #RRGGBB, so an opaque color drops its alpha
const OPAQUE = /^(#[0-9A-F]{6})FF$/i;

function ColorField({id, value, placeholder, error, disabled, onChange}: {
    id: string
    value?: string | null
    placeholder: string
    error?: string
    disabled?: boolean
    onChange: (color: string | null) => void
}) {
    return (
        <Stack sx={{width: '100%'}}>
            <NewColorPicker
                id={id}
                value={value ?? ''}
                placeholder={placeholder}
                showClear
                disabled={disabled}
                onChange={color => onChange(color?.replace(OPAQUE, '$1') || null)}
            />
            {!!error && <FormHelperText error sx={{mx: '14px'}}>{error}</FormHelperText>}
        </Stack>
    );
}

/** Design configuration fields: layout variant, corner radius, brand colors, and imagery. */
const DigitalSigningDesignFields = ({design, onChange, errors, disabled}: Props) => {
    const {t, i18n} = useTranslation();
    const set = (patch: Partial<DigitalSigningDesign>) => onChange({...design, ...patch});

    const photoLayout = design.variant === 'HERO' || design.variant === 'BACKGROUND' ? design.variant : undefined;
    const photoRows = photoLayout && PHOTO_ROWS[photoLayout];
    const percent = new Intl.NumberFormat(i18n?.language, {style: 'percent'});

    return (
        <Stack gap={{xs: 1.5, sm: 1}}>
            <Property title={t('Layout')} tooltip={t('Paper: the logo centered on warm paper. Hero: a photo (without one, the logo), the details on a colored band, the rest on a white card. Background: the photo behind the whole page under a veil, everything on a white card.')}>
                <TextField
                    select
                    size={'small'}
                    fullWidth
                    value={design.variant}
                    onChange={event => set({variant: event.target.value as DigitalSigningVariant})}
                    disabled={disabled}
                    inputProps={{'aria-label': t('Layout')}}
                >
                    {LAYOUTS.map(layout => (
                        <MenuItem key={layout.value} value={layout.value}>{t(layout.label, {context: 'layout'})}</MenuItem>
                    ))}
                </TextField>
            </Property>

            <Property title={t('Corners')} tooltip={t('Applies to the tip buttons, the fields, the card and the send button.')}>
                <TextField
                    select
                    size={'small'}
                    fullWidth
                    value={design.cornerStyle}
                    onChange={event => set({cornerStyle: event.target.value as DigitalSigningCornerStyle})}
                    disabled={disabled}
                    inputProps={{'aria-label': t('Corners')}}
                >
                    {CORNERS.map(corner => (
                        <MenuItem key={corner.value} value={corner.value}>{t(corner.label)}</MenuItem>
                    ))}
                </TextField>
            </Property>

            <Property title={t('Primary color')} tooltip={t('Buttons and the chosen tip. Empty: black.')}>
                <ColorField
                    id={'digital-signing-primary'}
                    value={design.primaryColor}
                    placeholder={SIGNING_INK}
                    error={errors?.primaryColor && t(errors.primaryColor)}
                    disabled={disabled}
                    onChange={color => set({primaryColor: color})}
                />
            </Property>

            {!!photoRows && (
                <Property title={t('Panel color')} tooltip={t(photoRows.panelTooltip)}>
                    <ColorField
                        id={'digital-signing-panel'}
                        value={design.panelColor}
                        placeholder={photoRows.panelPlaceholder}
                        error={errors?.panelColor && t(errors.panelColor)}
                        disabled={disabled}
                        onChange={color => set({panelColor: color})}
                    />
                </Property>
            )}

            <Property title={t('Logo')} tooltip={t('Replaces the organization\'s logo on the page.')} labelAlignment={'top'}>
                <ImageUploadField
                    id={'digital-signing-logo'}
                    image={design.logoMedia ?? undefined}
                    setImage={(media?: Media) => set({logoMedia: media ?? null})}
                    disabled={disabled}
                    width={230}
                    height={230}
                    ratio={'free'}
                    compact
                    supportSvg
                />
            </Property>

            {!!photoRows && (
                <Property title={t(photoRows.photo)} tooltip={t(photoRows.photoTooltip)} labelAlignment={'top'}>
                    {/* Cut to 16:9 on upload: Hero shows it whole, Background centered */}
                    <ImageUploadField
                        id={'digital-signing-hero'}
                        image={design.heroMedia ?? undefined}
                        setImage={(media?: Media) => set({heroMedia: media ?? null})}
                        disabled={disabled}
                        ratio={'landscape'}
                        compact
                    />
                </Property>
            )}

            {design.variant === 'BACKGROUND' && (
                <Property title={t('Image opacity')} tooltip={t('How strongly the photo shows through the veil.')}>
                    <TextField
                        select
                        size={'small'}
                        fullWidth
                        value={design.heroOpacity ?? DEFAULT_HERO_OPACITY}
                        onChange={event => set({heroOpacity: Number(event.target.value)})}
                        disabled={disabled}
                        inputProps={{'aria-label': t('Image opacity')}}
                    >
                        {HERO_OPACITY_PRESETS.map(preset => (
                            <MenuItem key={preset.percent} value={preset.percent}>
                                {t(preset.label)}
                                <Box component={'span'} sx={{ml: 1, color: 'text.secondary'}}>{percent.format(preset.percent / 100)}</Box>
                            </MenuItem>
                        ))}
                    </TextField>
                </Property>
            )}
        </Stack>
    );
};

export default DigitalSigningDesignFields;
